import React, { type JSX } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import type { Project } from "../../types/redux";

const CVC_BLUE = "#234E81";

const NAME_CLASS_BASE =
  "font-medium hover:opacity-80 hover:underline focus:outline-hidden focus:underline transition-colors duration-200 animate-fade-in";
const CHEVRON_CLASS_BASE =
  "rounded-sm hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors duration-200";

/**
 * The same project name + chat-options chevron is shown at three breakpoints,
 * each with its own container and truncation rules.
 */
export type ProjectNameVariant = "desktop" | "tablet" | "mobile";

interface VariantStyles {
  container: string;
  name: string;
  chevron: string;
  /** Desktop keeps the historic positioning wrapper around the chevron. */
  wrapChevron: boolean;
}

const VARIANTS: Record<ProjectNameVariant, VariantStyles> = {
  desktop: {
    container: "hidden sm:flex items-center gap-2 tablet:hidden",
    name: `text-sm sm:text-base ${NAME_CLASS_BASE}`,
    chevron: `p-1 ${CHEVRON_CLASS_BASE}`,
    wrapChevron: true,
  },
  tablet: {
    container:
      "hidden tablet:flex items-center justify-center gap-2 absolute left-1/2 -translate-x-1/2 max-w-[40%]",
    name: `text-sm sm:text-base ${NAME_CLASS_BASE} truncate`,
    chevron: `p-1 shrink-0 ${CHEVRON_CLASS_BASE}`,
    wrapChevron: false,
  },
  mobile: {
    container: "sm:hidden flex items-center gap-2 px-4 pb-2 bg-white",
    name: `text-sm ${NAME_CLASS_BASE} truncate`,
    chevron: `p-1 shrink-0 ${CHEVRON_CLASS_BASE}`,
    wrapChevron: false,
  },
};

export interface ProjectNameBarProps {
  variant: ProjectNameVariant;
  project: Project | null;
  /** Changing this remounts the name button, replaying its fade-in. */
  animationKey: number;
  isMenuOpen: boolean;
  onOpenSummary: () => void;
  onToggleMenu: (event: React.MouseEvent<HTMLElement>) => void;
}

export default function ProjectNameBar({
  variant,
  project,
  animationKey,
  isMenuOpen,
  onOpenSummary,
  onToggleMenu,
}: ProjectNameBarProps): JSX.Element | null {
  if (!project?.name) return null;

  const styles = VARIANTS[variant];

  const chevron = (
    <button
      onClick={onToggleMenu}
      className={styles.chevron}
      aria-label="Chat options"
      title="Chat options"
    >
      <ChevronDownIcon
        className={`h-4 w-4 transition-transform duration-200 ${isMenuOpen ? "rotate-180" : ""}`}
        style={{ color: CVC_BLUE }}
      />
    </button>
  );

  return (
    <div className={styles.container}>
      <button
        key={animationKey}
        onClick={onOpenSummary}
        className={styles.name}
        style={{ color: CVC_BLUE }}
        aria-label="View project summary"
      >
        {project.name}
      </button>

      {styles.wrapChevron ? <div className="relative">{chevron}</div> : chevron}
    </div>
  );
}
